import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck } from "lucide-react";
import ProductCard from "@/components/site/ProductCard";

const CATS = [
  { key: "all", label: "All" },
  { key: "cups", label: "Cups" },
  { key: "shakes", label: "Shakes" },
  { key: "tubs", label: "Family Tubs" },
];

export default function Menu() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = (cat === "all" ? products : products.filter((p) => p.category === cat)).filter((p) => p.category !== "boxes");

  return (
    <main data-testid="menu-page" className="mx-auto max-w-[1400px] px-4 pb-28 pt-8 md:px-10 md:pb-32 md:pt-16">
      <div className="grid md:grid-cols-12 gap-10 items-end border-b border-line pb-10">
        <div className="md:col-span-7">
          <div className="label-xs text-espresso-light">The Menu</div>
          <h1 className="mt-4 font-serif text-4xl leading-[0.95] tracking-tight text-espresso md:text-7xl">
            Order online.
          </h1>
          <div
            data-testid="fssai-badge"
            className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/50 bg-gold/10 text-espresso"
            title="Licensed by the Food Safety and Standards Authority of India"
          >
            <ShieldCheck size={14} strokeWidth={1.75} className="text-gold-hover" />
            <span className="label-xs">
              Licensed by FSSAI · <span className="text-espresso font-semibold tracking-wider">22726699000251</span>
            </span>
          </div>
        </div>
        <p className="md:col-span-5 text-espresso-light leading-relaxed">
          Every product is made the same day it reaches you. Choose a delivery or pickup slot at checkout.
        </p>
      </div>

      <div className="no-scrollbar sticky top-16 z-20 -mx-4 mt-8 flex gap-2 overflow-x-auto border-y border-line bg-ivory px-4 py-3 md:static md:mx-0 md:mt-10 md:flex-wrap md:gap-4 md:border-0 md:bg-transparent md:p-0" data-testid="menu-filters">
        {CATS.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            data-testid={`menu-filter-${c.key}`}
            className={`label-xs shrink-0 rounded-full border px-4 py-3 transition-colors duration-200 ${
              cat === c.key ? "bg-espresso text-ivory border-espresso" : "border-line text-espresso-light hover:text-espresso hover:border-espresso"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:mt-14 md:grid-cols-2 md:gap-x-8 md:gap-y-16 lg:grid-cols-3">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                data-testid={`menu-skeleton-${i}`}
                className="animate-pulse"
              >
                <div className="aspect-[4/5] bg-ivory-100 border border-line" />
                <div className="mt-4 h-4 w-2/3 bg-ivory-100" />
                <div className="mt-2 h-3 w-1/3 bg-ivory-100" />
              </div>
            ))
          : filtered.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
      {!loading && filtered.length === 0 && (
        <p
          data-testid="menu-empty"
          className="mt-20 text-center text-espresso-light font-serif italic text-xl"
        >
          Nothing in this category yet.
        </p>
      )}
    </main>
  );
}
